import { Card, CardHeader, CardContent, Button } from '@mui/material';
import { DeviceBackOfficeDeliveryIcon } from 'components/Devices/DeviceBackOfficeDeliveryIcon/DeviceBackOfficeDeliveryIcon';
import { DeviceBackOfficeSyncIcon } from 'components/Devices/DeviceBackOfficeSyncIcon/DeviceBackOfficeSyncIcon';
import { FormText, FormInputGroup, FormCheckbox } from 'components/form';
import {
    getBackOfficeAccountNumber,
    getBackOfficeAccountNumberLabel,
    getBackOfficeDeliveryIcon,
    getBackOfficeLabel,
    getBackOfficeSyncIcon,
    getBackOfficeTankNumber,
    getBackOfficeTankNumberLabel,
    useLazyAddsSyncCustomerQuery,
    useLazyAddsSyncTankQuery,
    useLazyCargasSyncCustomerQuery,
    useLazyCargasSyncTankQuery,
    useLazyGetCargasDeliveryStatusQuery,
} from 'store/services/third-party-backoffice';
import {
    SupplierInfo,
    Device,
    ThirdPartyBackOfficeCustomerInfo,
    ThirdPartyBackOfficeTankInfo,
    BackOfficeDeliveryIconConfig,
} from 'types';
import GetAppIcon from '@mui/icons-material/GetApp';
import styles from './DeviceConfigBackofficeSync.module.scss';
import parentStyles from '../../DeviceConfig.module.scss';
import { useEffect, useState } from 'react';
import LoadingButton from '@mui/lab/LoadingButton';
import { BackofficePullModal } from './modals/BackofficePullModal/BackofficePullModal';
import { useDispatch } from 'react-redux';
import { activateSnackbar } from 'store/features/ui';
import { useLazyPatchUpdateDeviceQuery } from 'store/services';
import {
    getObjectDiff,
    recursivelyNullifyUndefinedValues,
} from 'helpers/general';
import { useFormContext } from 'react-hook-form';

export interface DeviceConfigBackofficeSyncProps {
    supplierInfo: SupplierInfo;
    device: Device;
    supplierId: string;
    setOpenBackofficeDeliveryDialog: React.Dispatch<
        React.SetStateAction<boolean>
    >;
    /** Callback to alert parent component about device updates. */
    onDeviceUpdate: React.Dispatch<React.SetStateAction<Device | undefined>>;
}

/** Device Details > Config backoffice sync section. */
export const DeviceConfigBackofficeSync = ({
    supplierInfo,
    device,
    supplierId,
    setOpenBackofficeDeliveryDialog,
    onDeviceUpdate,
}: DeviceConfigBackofficeSyncProps) => {
    const dispatch = useDispatch();
    const { watch, setValue, getValues } = useFormContext();

    const [checkCargasDeliveryStatusQuery] =
        useLazyGetCargasDeliveryStatusQuery();
    const [updateDeviceQuery] = useLazyPatchUpdateDeviceQuery();

    const [accountNumber, setAccountNumber] = useState('');
    const [tankNumber, setTankNumber] = useState('');
    const [accountNumberLabel, setAccountNumberLabel] = useState('');
    const [tankNumberLabel, setTankNumberLabel] = useState('');
    const [backofficeLabel, setBackofficeLabel] = useState('');
    const [isSyncing, setIsSyncing] = useState(false);
    const [customerInfo, setCustomerInfo] =
        useState<ThirdPartyBackOfficeCustomerInfo>();
    const [tankInfo, setTankInfo] = useState<ThirdPartyBackOfficeTankInfo>();
    const [completeImport, setCompleteImport] = useState(false);

    const accountNumberWatch = watch('backOfficeAccountNumber');
    const tankNumberWatch = watch('backOfficeTankNumber');
    const accountIdWatch = watch('accountId');

    const [currentAccountId, setCurrentAccountId] = useState(
        getValues('accountId')
    );
    const [currentAccountNumber, setCurrentAccountNumber] = useState(
        getValues('backOfficeAccountNumber')
    );
    const [currentTankNumber, setCurrentTankNumber] = useState(
        getValues('backOfficeTankNumber')
    );
    // Detect which of account id, account number, or tank number was changed by the user.
    // Update the other two fields accordingly (such that accountId = ${accountNumber}-${tankNumber}).
    useEffect(() => {
        const updateAccountId = (value: string) => {
            setCurrentAccountId(value);
            setValue('accountId', value);
        };

        const updateAccountNumber = (value: string) => {
            setCurrentAccountNumber(value);
            setValue('backOfficeAccountNumber', value);
        };

        const updateTankNumber = (value: string) => {
            setCurrentTankNumber(value);
            setValue('backOfficeTankNumber', value);
        };

        // User typed in the account id field.
        if (currentAccountId !== accountIdWatch) {
            setCurrentAccountId(accountIdWatch);
            updateAccountNumber(getBackOfficeAccountNumber(accountIdWatch));
            updateTankNumber(getBackOfficeTankNumber(accountIdWatch));
        }
        // User typed in the account number field.
        if (currentAccountNumber !== accountNumberWatch) {
            setCurrentAccountNumber(accountNumberWatch);
            updateAccountId(`${accountNumberWatch}-${currentTankNumber}`);
        }
        // User typed in the tank number field.
        if (currentTankNumber !== tankNumberWatch) {
            setCurrentTankNumber(tankNumberWatch);
            updateAccountId(`${currentAccountNumber}-${tankNumberWatch}`);
        }
    }, [
        setValue,
        accountIdWatch,
        accountNumberWatch,
        tankNumberWatch,
        currentAccountId,
        currentAccountNumber,
        currentTankNumber,
    ]);

    // Set backoffice labels using terminology consistent with the specific third-party backoffice product.
    useEffect(() => {
        if (supplierInfo && supplierInfo.backoffice) {
            setBackofficeLabel(getBackOfficeLabel(supplierInfo.backoffice));
            setAccountNumberLabel(
                getBackOfficeAccountNumberLabel(supplierInfo.backoffice)
            );
            setTankNumberLabel(
                getBackOfficeTankNumberLabel(supplierInfo.backoffice)
            );
        }
    }, [supplierInfo]);

    // Set backoffice account/customer and tank numbers.
    useEffect(() => {
        if (device) {
            setAccountNumber(getBackOfficeAccountNumber(device.accountId));
            setTankNumber(getBackOfficeTankNumber(device.accountId));
        }
    }, [device]);

    // If the supplier uses cargas, we need to query to get the latest cargas delivery info and update the device object.
    useEffect(() => {
        if (
            device &&
            tankNumber &&
            backofficeLabel &&
            backofficeLabel.toLowerCase() === 'cargas'
        ) {
            checkCargasDeliveryStatusQuery({ supplierId, tankNumber }).then(
                (result) => {
                    if (result.isSuccess && result.data) {
                        const hasDelivery = result.data?.tank?.HasOpenDelivery;
                        const id = result.data?.tank.TankMonitorID;

                        // If the deviceIds match, update backoffice delivery fields to the latest values.
                        if (id && id === device.deviceId) {
                            const deviceUpdates: Partial<Device> = {
                                _has_delivery: hasDelivery,
                            };

                            // Get updated delivery icon now that `_has_delivery` is `true`.
                            const newIconConfig: BackOfficeDeliveryIconConfig =
                                getBackOfficeDeliveryIcon({
                                    ...device,
                                    ...deviceUpdates,
                                });

                            // Save backoffice delivery icon config updates.
                            deviceUpdates._delivery_icon =
                                newIconConfig.delivery_icon;
                            deviceUpdates._delivery_style =
                                newIconConfig.delivery_style;
                            deviceUpdates._bo_delivery_msg =
                                newIconConfig.bo_delivery_msg;

                            // Make query to update this device with these new values.
                            updateDeviceQuery({
                                deviceId: device.deviceId,
                                updatedFields: deviceUpdates,
                            }).then((result) => {
                                if (result.isSuccess && result.data) {
                                    // Alert parent component that the device has changed.
                                    onDeviceUpdate(result.data);
                                }
                            });
                        }
                    }
                }
            );
        }
    }, [
        backofficeLabel,
        device,
        tankNumber,
        supplierId,
        checkCargasDeliveryStatusQuery,
        updateDeviceQuery,
        onDeviceUpdate,
    ]);

    // If user confirms import, save imported backoffice customer + tank data to the device.
    useEffect(() => {
        if (completeImport) {
            const importedData: Partial<Device> = {
                customer_name: customerInfo?.customer_name,
                capacity: tankInfo?.capacity,
                address: tankInfo?.address,
                name: tankInfo?.tank_name,
                accountId: `${currentAccountNumber}-${currentTankNumber}`,
            };
            if (backofficeLabel.toLowerCase() === 'cargas') {
                importedData.group = tankInfo?.group;
                importedData.delivery_type = tankInfo?.delivery_type;
            }

            // Find which values change from what's currently on the device vs the new imported data.
            let deviceUpdates: Partial<Device> = getObjectDiff(
                device,
                importedData
            );

            // If optional fields were previously filled and then deleted by the user, we need to set that undefined field to null to have the data update properly.
            deviceUpdates = recursivelyNullifyUndefinedValues(deviceUpdates);

            // Save imported customer and tank data.
            updateDeviceQuery({
                deviceId: device.deviceId,
                updatedFields: deviceUpdates,
            }).then((result) => {
                if (result.isSuccess && result.data) {
                    // Alert parent component that the device has changed.
                    onDeviceUpdate(result.data);
                }
            });
            // Reset variables.
            setCompleteImport(false);
        }
    }, [
        backofficeLabel,
        completeImport,
        currentAccountNumber,
        currentTankNumber,
        customerInfo,
        device,
        onDeviceUpdate,
        tankInfo,
        updateDeviceQuery,
    ]);

    const checkBackofficeDelivery = () => {
        const isInternalUser =
            supplierInfo.supplierId?.toString() === '0' ? true : false;
        if (
            device._bo_sync_complete &&
            !device._has_delivery &&
            !isInternalUser
        ) {
            // Show BackofficeDeliveryModal
            setOpenBackofficeDeliveryDialog(true);
        }
    };

    // Backoffice Sync/Import
    const [addsSyncCustomerQuery] = useLazyAddsSyncCustomerQuery();
    const [addsSyncTankQuery] = useLazyAddsSyncTankQuery();

    const [cargasSyncCustomerQuery] = useLazyCargasSyncCustomerQuery();
    const [cargasSyncTankQuery] = useLazyCargasSyncTankQuery();

    const [backofficePullDialogOpen, setBackofficePullDialogOpen] =
        useState(false);
    const openBackofficePullDialog = () => {
        setBackofficePullDialogOpen(true);
    };
    const closeBackofficePullDialog = () => {
        setBackofficePullDialogOpen(false);
    };

    const handleBackofficeSyncError = () => {
        setIsSyncing(false);
        dispatch(
            activateSnackbar({
                message: `Failed to reach the ${backofficeLabel} server. Check your configuration settings.`,
                severity: 'error',
            })
        );
    };

    // Initiate the sync/import of backoffice data. Does not overwrite any data until the user confirms the import on the modal.
    const syncBackoffice = () => {
        if (!backofficeLabel) return;

        if (backofficeLabel.toLowerCase() === 'cargas') {
            setIsSyncing(true);
            syncCargas();
        } else if (backofficeLabel.toLowerCase() === 'adds') {
            setIsSyncing(true);
            syncAdds();
        }
    };

    /** Makes network calls to sync latest Adds third-party backoffice data. */
    const syncAdds = () => {
        // Pull latest adds customer/account data for the device.
        addsSyncCustomerQuery({
            supplierId: supplierId,
            customerId: currentAccountNumber,
        })
            .then((customerRes) => {
                if (customerRes.isSuccess) setCustomerInfo(customerRes.data);
                else if (customerRes.isError) handleBackofficeSyncError();

                // Pull adds latest tank data for the device.
                addsSyncTankQuery({
                    supplierId,
                    tankNumber: currentTankNumber,
                    customerId: currentAccountNumber,
                })
                    .then((tankRes) => {
                        if (tankRes.isSuccess) {
                            setTankInfo(tankRes.data);
                            // Display the latest tank and customer/account data on modal.
                            openBackofficePullDialog();
                        } else if (tankRes.isError) {
                            handleBackofficeSyncError();
                        }
                        setIsSyncing(false);
                    })
                    .catch(() => {
                        handleBackofficeSyncError();
                    });
            })
            .catch(() => {
                handleBackofficeSyncError();
            });
    };

    /** Makes network calls to sync latest Cargas third-party backoffice data. */
    const syncCargas = () => {
        // Pull latest cargas customer/account data for the device.
        cargasSyncCustomerQuery({
            supplierId,
            customerId: currentAccountNumber,
        })
            .then((customerRes) => {
                if (customerRes.isSuccess) setCustomerInfo(customerRes.data);
                else if (customerRes.isError) handleBackofficeSyncError();

                // Pull latest cargas tank data for the device.
                cargasSyncTankQuery({
                    supplierId,
                    tankNumber: currentTankNumber,
                })
                    .then((tankRes) => {
                        if (tankRes.isSuccess) {
                            setTankInfo(tankRes.data);
                            // Display the latest tank and customer/account data on modal.
                            openBackofficePullDialog();
                        } else if (tankRes.isError) {
                            handleBackofficeSyncError();
                        }
                        setIsSyncing(false);
                    })
                    .catch(() => {
                        handleBackofficeSyncError();
                    });
            })
            .catch(() => {
                handleBackofficeSyncError();
            });
    };

    return (
        <div
            data-testid="DeviceConfigBackofficeSync"
            className={parentStyles.section}
        >
            <Card className={parentStyles.card}>
                <div className={styles.header}>
                    <CardHeader title="BackOffice Sync" />
                    <div className={styles.syncIconLabel}>
                        <p>{backofficeLabel}</p>

                        <DeviceBackOfficeSyncIcon
                            iconConfig={getBackOfficeSyncIcon(device)}
                        />
                    </div>
                </div>
                <CardContent>
                    {/* Account and Tank Numbers */}
                    <FormInputGroup>
                        <FormText
                            field="backOfficeAccountNumber"
                            label={`${backofficeLabel} ${accountNumberLabel}`}
                        />
                        <FormText
                            field="backOfficeTankNumber"
                            label={`${backofficeLabel} ${tankNumberLabel}`}
                        />
                    </FormInputGroup>

                    <FormInputGroup>
                        <FormText field="accountId" label="Account ID" />
                    </FormInputGroup>

                    {/* Delivery Button , Auto Sync button, and Import button row */}
                    <div className={styles.buttonRow}>
                        {supplierInfo.backoffice === 'cargas' && (
                            <Button
                                onClick={checkBackofficeDelivery}
                                variant="outlined"
                                endIcon={
                                    <DeviceBackOfficeDeliveryIcon
                                        iconConfig={getBackOfficeDeliveryIcon(
                                            device
                                        )}
                                    />
                                }
                            >
                                Delivery
                            </Button>
                        )}

                        <div className={styles.buttonGroup}>
                            {supplierInfo.backoffice === 'cargas' && (
                                <Button variant="outlined">
                                    <FormCheckbox
                                        field="bo_sync_config"
                                        label="Auto Sync"
                                    />
                                </Button>
                            )}

                            {(supplierInfo.backoffice === 'cargas' ||
                                supplierInfo.backoffice === 'adds') && (
                                <LoadingButton
                                    variant="outlined"
                                    onClick={syncBackoffice}
                                    endIcon={<GetAppIcon />}
                                    loading={isSyncing}
                                >
                                    Import
                                </LoadingButton>
                            )}
                        </div>
                    </div>
                </CardContent>
            </Card>

            {tankInfo && customerInfo && (
                <BackofficePullModal
                    open={backofficePullDialogOpen}
                    onClose={closeBackofficePullDialog}
                    backofficeLabel={backofficeLabel}
                    tankInfo={tankInfo}
                    customerInfo={customerInfo}
                    device={device}
                    onConfirmImport={setCompleteImport}
                    newAccountNumber={currentAccountNumber}
                    newTankNumber={currentTankNumber}
                    oldAccountNumber={accountNumber}
                    oldTankNumber={tankNumber}
                    tankNumberLabel={tankNumberLabel}
                    accountNumberLabel={accountNumberLabel}
                />
            )}
        </div>
    );
};
